'use client';
import React, { useEffect, useRef } from 'react';

const services = [
  { num: '01', title: 'Executive Résumé Writing', desc: 'A bespoke, performance-guaranteed résumé engineered to open doors at the world\'s most competitive organisations. Every word earns its place.', price: 'From $695' },
  { num: '02', title: 'LinkedIn Profile Optimisation', desc: 'Transform your LinkedIn into a 24/7 recruiter magnet keyword-optimised, authority-positioned, and precisely aligned with your target role.', price: 'From $395' },
  { num: '03', title: 'Career Transition Coaching', desc: 'Strategic, personalised coaching from the world\'s most experienced career coach. Clarity, direction, and a proven roadmap wherever you are in your career.', price: 'From $450 / session' },
  { num: '04', title: 'Interview Preparation', desc: 'Master every interview format with the author who wrote the definitive book on interview technique. Walk in prepared, walk out with the offer.', price: 'From $395 / session' },
  { num: '05', title: 'Salary Negotiation Coaching', desc: 'Negotiate from a position of strength. Our clients average $28,000+ in additional salary through structured negotiation coaching.', price: 'From $395 / session' },
  { num: '06', title: 'Cover Letter Writing', desc: 'Compelling, individually crafted cover letters that compel hiring managers to read your résumé and pick up the phone.', price: 'From $195' },
  { num: '07', title: 'Executive Biography', desc: 'Authority-building professional bios for board positions, speaking engagements, media profiles, and high-stakes business development.', price: 'From $295' },
  { num: '08', title: 'Job Search Strategy', desc: 'A systematic, intelligence-led approach to your job search identifying targets, building networks, and creating sustained momentum.', price: 'From $450 / session' },
  { num: '09', title: 'International CV Writing', desc: 'CVs tailored for international markets across 63+ countries regionally formatted, culturally fluent, and strategically positioned.', price: 'From $795' },
  { num: '10', title: 'LinkedIn Coaching', desc: 'One-on-one coaching to maximise your LinkedIn presence, grow a relevant professional network, and generate consistent inbound recruiter interest.', price: 'From $295 / session' },
  { num: '11', title: 'SWOT Analysis', desc: 'A comprehensive personal SWOT analysis of your Strengths, Weaknesses, Opportunities, and Threats to give you a clear, strategic picture of where you stand and how to position yourself for your next role.', price: 'From $350' },
  { num: '12', title: '30-60-90 Day Action Plan', desc: 'A structured, milestone-driven career action plan covering your first 30, 60, and 90 days in a new role or job search. Designed to accelerate your transition and set you up for measurable early wins.', price: 'From $425' },
{ num: '13', title: 'Candidate Validation Documents', desc: 'Comprehensive validation documentation that verifies and presents your professional credentials, achievements, and competencies to give employers the confidence to hire you with certainty.', price: 'From $425' },
];

export default function ServicesGrid() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1 }
    );
    if (headRef.current) observer.observe(headRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="section-cream relative py-24 lg:py-32 overflow-hidden" id="services" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Header */}
        <div ref={headRef} className="reveal-section mb-16">
          <span className="section-index block mb-4" style={{ color: 'rgba(74,25,66,0.4)' }}>
            WHAT WE OFFER
          </span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2
              className="font-fraunces font-medium"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)', color: '#4A1942', lineHeight: '1.05' }}
            >
              Career Services Built for<br />
              <span style={{ color: '#C9A84C', fontStyle: 'italic' }}>Serious Professionals</span>
            </h2>
            <p className="max-w-sm text-base leading-relaxed" style={{ color: '#4A4A4A' }}>
              Every service is personally delivered customised to your goals, backed by 25+ years of executive expertise and a performance guarantee.
            </p>
          </div>
        </div>

        {/* Services list */}
        <div className="flex flex-col divide-y" style={{ borderColor: 'rgba(74,25,66,0.1)' }}>
          {services.map((service, i) => (
            <div
              key={service.num}
              className="group flex flex-col sm:flex-row sm:items-center gap-4 py-6 cursor-default transition-all duration-300 hover:bg-plum/5 -mx-4 px-4 rounded-sm"
            >
              <span className="font-mono-custom text-xs flex-shrink-0 w-8" style={{ color: 'rgba(74,25,66,0.35)' }}>
                {service.num}
              </span>
              <div className="flex-1">
                <h3 className="font-semibold text-base mb-1 group-hover:text-plum transition-colors" style={{ color: '#4A1942' }}>
                  {service.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: '#6A6A6A' }}>{service.desc}</p>
              </div>
              <div className="flex items-center gap-4 flex-shrink-0">
                <span className="text-sm font-semibold font-mono-custom" style={{ color: '#C9A84C' }}>{service.price}</span>
                <a
                  href="/contact"
                  className="text-xs font-semibold tracking-wide px-4 py-2 rounded-sm border transition-all duration-200"
                  style={{ borderColor: 'rgba(74,25,66,0.2)', color: '#4A1942' }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.background = '#4A1942';
                    (e.currentTarget as HTMLElement).style.color = '#F5F0E8';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                    (e.currentTarget as HTMLElement).style.color = '#4A1942';
                  }}
                >
                  Get Started
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Performance guarantee banner */}
        <div
          className="mt-14 p-8 rounded-sm text-center"
          style={{ background: 'rgba(74,25,66,0.05)', border: '1px solid rgba(74,25,66,0.12)' }}
        >
          <p className="font-fraunces text-lg font-light italic mb-3" style={{ color: '#4A1942' }}>
            "Our résumés come backed with a performance guarantee. Your résumé will work or we revise it at no charge until it does."
          </p>
          <a href="/contact" className="cta-gold px-8 py-3.5 rounded-sm text-sm font-semibold tracking-wide inline-flex items-center gap-2 mt-4">
            Get Started
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
